import { Agent, AgentDetails, Reason } from '@propertypal/shared/src/types/agents';
import BranchBox from '@propertypal/web-ui/src/agents/BranchBox';
import BranchOverview from '@propertypal/web-ui/src/agents/BranchOverview';
import { Hr } from '@propertypal/web-ui/src/layout';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import AgentAccreditations from '@propertypal/web-ui/src/property/AgentAccreditations';
import PropertyGrid from '@propertypal/web-ui/src/property/PropertyGrid';
import { Heading, MidHeading, SubHeading } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import GoogleMap, { centerToBounds, InfoWindow } from '../map/GoogleMap';
import {
  Accreditations,
  AgentLogo,
  BranchesWrapper,
  FormBox,
  FormContainer,
  LinkList,
  OtherBranches,
  SummaryBox,
  TitleBox,
} from './AgentDetails.style';
import AgentEmailForm from './AgentEmailForm';

interface Props {
  reasons: Reason[];
  agentDetails: AgentDetails;
}

const setInitialRegion = (infoWindow: InfoWindow[]) => {
  if (infoWindow.length > 1) {
    const latLngs = infoWindow.map((branch) => ({
      lat: branch.lat,
      lng: branch.lng,
    }));

    centerToBounds(latLngs, 120);
  }
};

const getInfoWindowHtml = (agent: Agent) => {
  if (agent.logo?.standard) {
    return { image: agent.logo.standard };
  }

  return { text: agent.organisation };
};

const AgentDetailsBox: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const [branch] = props.agentDetails.branches;
  const emailEnabled = !!props.agentDetails.branches.find((agent) => agent.emailEnabled);
  const showAccountContact = !!props.agentDetails.branches.find((agent) => agent.showAccountContact);
  const infoWindows = props.agentDetails.branches
    .filter((agent) => !!agent.coordinate)
    .map((agent) => ({
      content: getInfoWindowHtml(agent),
      lat: agent.coordinate?.latitude || 0,
      lng: agent.coordinate?.longitude || 0,
    }));

  return (
    <>
      <TitleBox data-testid="agent-details-page">
        <AgentLogo src={branch.logo?.standard} alt={branch.organisation} />
        <Heading align="center">{props.agentDetails.description.short}</Heading>
      </TitleBox>

      <Hr />

      <PageWrapper>
        <SummaryBox>
          <div style={{ flex: 1, marginBottom: 15 }}>
            <BranchesWrapper>
              {props.agentDetails.branches.map((agent) => (
                <BranchOverview
                  key={agent.accountNumber}
                  agent={agent}
                  compact={props.agentDetails.branches.length > 1}
                />
              ))}
            </BranchesWrapper>

            {/* <AgentAwards
              awards={branch.propertypalAwards}
              containerStyle={{ marginTop: 30, marginBottom: 30 }}
            /> */}

            {branch.coordinate && infoWindows.length === 1 && (
              <GoogleMap
                width="100%"
                height={340}
                lat={branch.coordinate.latitude}
                lng={branch.coordinate.longitude}
                zoom={14}
                infoWindows={infoWindows}
              />
            )}
          </div>

          {emailEnabled && showAccountContact && (
            <FormContainer data-testid="enquiry-form">
              <SubHeading align="center" fontSize={20}>
                Request a valuation or make a general enquiry
              </SubHeading>

              <FormBox>
                <AgentLogo src={branch.logo?.standard} alt={branch.organisation} />

                <AgentEmailForm
                  agents={props.agentDetails.branches}
                  reasons={props.reasons}
                  loggedInUser={props.agentDetails.loggedInUser}
                  compact
                />
              </FormBox>
            </FormContainer>
          )}
        </SummaryBox>
      </PageWrapper>

      {infoWindows.length > 1 && (
        <GoogleMap width="100%" height={500} infoWindows={infoWindows} onLoad={() => setInitialRegion(infoWindows)} />
      )}

      {!!props.agentDetails.accreditations?.length && (
        <Accreditations>
          <PageWrapper>
            <h3>Accreditations</h3>

            <AgentAccreditations
              accreditations={props.agentDetails.accreditations}
              missingAccreditations={props.agentDetails.missingAccreditations}
              center
            />
          </PageWrapper>
        </Accreditations>
      )}

      <PageWrapper>
        {props.agentDetails.properties && props.agentDetails.properties.length > 0 && (
          <>
            <MidHeading mt={60} mb={30} data-testid="recently-added">
              Recently Added Properties
            </MidHeading>

            <PropertyGrid containerStyle={{ padding: 0 }} properties={props.agentDetails.properties} />
          </>
        )}

        {props.agentDetails.stats.category && props.agentDetails.stats.category.length > 0 && (
          <>
            <MidHeading mt={60} mb={30} data-testid="more-properties">
              Browse more properties from {branch.organisation}
            </MidHeading>

            <LinkList>
              {props.agentDetails.stats.category.map((search) => (
                <Link key={search.searchUrl.url} href={search.searchUrl.url}>
                  {search.searchUrl.label}
                </Link>
              ))}
            </LinkList>
          </>
        )}
      </PageWrapper>

      {props.agentDetails.otherBranches && props.agentDetails.otherBranches.length > 0 && (
        <PageWrapper containerStyle={{ background: theme.backgroundLighter }}>
          <MidHeading mt={40}>Visit our other branches</MidHeading>

          <OtherBranches>
            {props.agentDetails.otherBranches.map((agent) => (
              <BranchBox key={agent.accountNumber} branch={agent} />
            ))}
          </OtherBranches>
        </PageWrapper>
      )}
    </>
  );
};

export default AgentDetailsBox;
